import { jwtDecode } from 'jwt-decode'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import YouTubePlayerUI from '../../Components/Youtube'
import socket from '../../utils/socket'
function extractVideoId(input) {
  if (!input) return null
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input
  try {
    const url = new URL(input)
    if (url.hostname.includes('youtu.be'))
      return url.pathname.slice(1).split('?')[0]
    if (url.searchParams.has('v')) return url.searchParams.get('v')
  } catch (err) {
    console.log(err)
  }
  return null
}

async function fetchVideoTitle(videoId) {
  try {
    if (!videoId) return null
    const res = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    )
    if (!res.ok) throw new Error('Video not found')
    const data = await res.json()
    return {
      thumbnail_url: data.thumbnail_url,
      title: data.title || 'Unknown Title',
      author_name: data.author_name,
    }
  } catch {
    return {
      thumbnail_url: '',
      title: 'Unknown Title',
      author_name: '',
    }
  }
}

export default function YouTubePlayer({ roomId }) {
  const playerRef = useRef(null)
  const [videoId, setVideoId] = useState('') //ZAkr0KFFLLs
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(100)
  const [newVideoInput, setNewVideoInput] = useState('')
  const [queue, setQueue] = useState([])
  const [resumeTime, setResumeTime] = useState(null)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [shouldPlayAfterSeek, setShouldPlayAfterSeek] = useState(false)
  const [pendingSeek, setPendingSeek] = useState(false)
  const [shouldSeekAndPlay, setShouldSeekAndPlay] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [seeking, setSeeking] = useState(false)
  const [autoPlayNext, setAutoPlayNext] = useState(false)
  // ① Add this line near the top of your component
  const [userClickedPause, setUserClickedPause] = useState(false)

  const handlePlayPauseClick = async (type) => {
    const time = await playerRef.current?.getCurrentTime()
    if (type === 'Play' || type === 'Pause') {
      socket.emit('saveState', {
        videoId,
        time,
        isPlaying: type === 'Play',
        roomId,
      })
    }
    if (isPlaying) {
      playerRef.current.pauseVideo()
      setUserClickedPause(true) // mark that this pause is real
    } else {
      playerRef.current.playVideo()
    }
  }
  const [thumbnailImage, setThumbnailImage] = useState({
    songTitle: '',
    songThumbnail: '',
    author_name: '',
  })

  const navigate = useNavigate()
  const token = localStorage.getItem('token')

  const handleRemoteAction = useCallback((action) => {
    const player = playerRef.current
    if (
      !player ||
      typeof player[
        action.type === 'Play' || action.type === 'Pause'
          ? 'playVideo'
          : action.type === 'Mute/Unmute'
          ? 'mute'
          : 'getVolume'
      ] !== 'function'
    ) {
      console.warn('Player method missing for', action.type)
      return setPendingAction(action)
    }
    switch (action.type) {
      case 'Play':
        player.playVideo()
        setIsPlaying(true)
        break
      case 'Pause':
        player.pauseVideo()
        setIsPlaying(false)
        break
      case 'Volume Up': {
        const currentVolume = player.getVolume()
        const newVolume = Math.min(100, currentVolume + 10)
        player.setVolume(newVolume)
        setVolume(newVolume)
        break
      }
      case 'Volume Down': {
        const currentVolume = player.getVolume()
        const newVolume = Math.max(0, currentVolume - 10)
        player.setVolume(newVolume)
        setVolume(newVolume)
        break
      }
      case 'Mute/Unmute':
        if (player?.isMuted()) {
          player.unMute()
          setIsMuted(false)
        } else {
          player.mute()
          setIsMuted(true)
        }
        break
      case 'VolumeChange':
        player.setVolume(action?.value)
        setVolume(action?.value)
        break
      default:
        console.warn('Unknown action type', action)
    }
  }, [])

  useEffect(() => {
    const fetchInitialSongInfo = async () => {
      if (!videoId) return
      const existingSong = queue.find(
        (songItem) => songItem.video_id === videoId
      )
      if (existingSong) {
        setThumbnailImage({
          songTitle: existingSong.title,
          songThumbnail: existingSong.thumbnail,
          author_name: existingSong.author,
        })
      } else {
        const data = await fetchVideoTitle(videoId)
        setThumbnailImage({
          songTitle: data.title,
          songThumbnail: data.thumbnail_url,
          author_name: data.author_name,
        })
      }
    }
    fetchInitialSongInfo()
  }, [videoId, queue])

  useEffect(() => {
    if (!roomId) return
    socket.emit('joinRoom', { roomId })
    socket.emit('requestFullSync', { roomId })

    socket.on('loadState', (state) => {
      if (!state?.video_id) return
      setVideoId(state.video_id)
      let time = state.time || 0
      const songInQueue = queue.find((song) => song.video_id === state.video_id)
      if (songInQueue) {
        setThumbnailImage({
          songTitle: songInQueue.title,
          songThumbnail: songInQueue.thumbnail,
          author_name: songInQueue.author,
        })
      }
      if (state.isPlaying && state.lastUpdated) {
        time += (Date.now() - state.lastUpdated) / 1000
      }
      setResumeTime(time)
      setShouldPlayAfterSeek(state.isPlaying)
      setPendingSeek(true)
    })
    socket.on('controlAction', (action) => {
      if (action.roomId && action.roomId !== roomId) return
      if (isPlayerReady) handleRemoteAction(action)
      else setPendingAction(action)
    })

    socket.on('updateQueue', (newQueue) => {
      const cleanQueue = (newQueue || []).filter(
        (item) =>
          typeof item.video_id === 'string' &&
          item.video_id.trim() !== '' &&
          typeof item.title === 'string' &&
          item.title.trim() !== ''
      )

      // Set queue only if it changed
      setQueue((prevQueue) => {
        const prevIds = prevQueue
          .map((s) => s.video_id)
          .sort()
          .join(',')
        const newIds = cleanQueue
          .map((s) => s.video_id)
          .sort()
          .join(',')
        if (prevIds !== newIds) return cleanQueue
        return prevQueue
      })

      // Handle videoId cleanup
      if (videoId && !cleanQueue.find((s) => s.video_id === videoId)) {
        setVideoId('')
        setResumeTime(null)
        setIsPlaying(false)
        setThumbnailImage({ songTitle: '', songThumbnail: '', author_name: '' })
      } else {
        const current = cleanQueue.find((s) => s.video_id === videoId)
        if (current) {
          setThumbnailImage({
            songTitle: current.title,
            songThumbnail: current.thumbnail,
            author_name: current.author,
          })
        }
      }
    })

    socket.on(
      'playFromQueue',
      ({ videoId: vid, time, roomId: eventRoomId }) => {
        if (eventRoomId && eventRoomId !== roomId) return
        setVideoId(vid)
        setResumeTime(time || 0)
        setShouldPlayAfterSeek(true)
        setPendingAction(null)
        setShouldSeekAndPlay(true)
        if (playerRef.current) playerRef.current.unMute()
      }
    )

    socket.on('isFirstClient', (flag) => {
      if (playerRef.current) {
        flag ? playerRef.current.unMute() : playerRef.current.mute()
      }
    })

    const handleSeek = ({ time }) => {
      if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
        const currentTime = playerRef.current.getCurrentTime()
        if (Math.abs(currentTime - time) > 1) {
          playerRef.current.seekTo(time, true)
        }
      }
    }
    socket.on('seek', handleSeek)
    return () => {
      socket.off('loadState')
      socket.off('controlAction')
      socket.off('updateQueue')
      socket.off('playFromQueue')
      socket.off('isFirstClient')
      socket.off('seek', handleSeek)
    }
  }, [roomId, isPlayerReady, handleRemoteAction, token])

  useEffect(() => {
    if (!roomId) return

    const fetchRoomInfo = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_SOCKET}/api/room/room-info/${roomId}`
        )
        const { queue: newQueue, state } = await res.json()

        // Validate and clean incoming queue
        const filteredQueue = (newQueue || []).filter(
          (item) =>
            typeof item.video_id === 'string' &&
            item.video_id.trim() !== '' &&
            typeof item.title === 'string' &&
            item.title.trim() !== ''
        )

        setQueue(filteredQueue) // Clean set
        setVideoId(state?.video_id || '')
        setResumeTime(state?.time || 0)
        setIsPlaying(state?.isPlaying || false)

        if (state?.videoId) {
          const playingSong = filteredQueue.find(
            (s) => s.video_id === state.videoId
          )
          if (playingSong) {
            setThumbnailImage({
              songTitle: playingSong.title,
              songThumbnail: playingSong.thumbnail_url,
              author_name: playingSong.author,
            })
          } else {
            setThumbnailImage({
              songTitle: '',
              songThumbnail: '',
              author_name: '',
            })
          }
        }
      } catch (err) {
        console.error('Failed to fetch room info:', err)
      }
    }

    fetchRoomInfo()
  }, [roomId])

  const handleClick = async (type) => {
    try {
      const time = await playerRef.current?.getCurrentTime()
      handleRemoteAction({ type, time })
      if (type === 'Mute/Unmute') {
        socket.emit('controlAction', { videoId, type, time, token, roomId })
      } else {
        socket.emit('controlAction', { videoId, type, time, token, roomId })
      }
      if (type === 'Play' || type === 'Pause') {
        socket.emit('saveState', {
          videoId,
          time,
          isPlaying: type === 'Play',
          roomId,
        })
      }
    } catch (err) {
      console.error('Error during handleClick:', err)
    }
  }

  const handleAddVideo = async (songFetchId) => {
    if (typeof songFetchId === 'string') {
      const { title, thumbnail_url, author_name } = await fetchVideoTitle(
        songFetchId
      )
      const newQueue = [
        ...queue,
        {
          video_id: songFetchId,
          title,
          thumbnail: thumbnail_url,
          author: author_name,
        },
      ]
      setQueue(newQueue)
      setNewVideoInput('')
      socket.emit('updateQueue', { newQueue, token, roomId }) //here room id is add because when add song from search funtionality
      return
    }
    const id = extractVideoId(newVideoInput.trim())
    if (!id) {
      alert('Invalid YouTube URL or ID')
      setNewVideoInput('')
      return
    }

    // Don't add if already in queue
    if (queue.find((v) => v.video_id === id)) {
      alert('Video already in queue')
      setNewVideoInput('')
      return
    }

    const { title, thumbnail_url, author_name } = await fetchVideoTitle(id)

    // Only add if all values are valid
    if (!title || !thumbnail_url || !author_name) {
      return alert('Invalid video metadata')
    }

    const newItem = {
      video_id: id,
      title,
      thumbnail: thumbnail_url,
      author: author_name,
    }
    const newQueue = [...queue, newItem]

    setQueue(newQueue)
    setNewVideoInput('')

    // 🔒 Ensure clean queue is sent
    const filteredQueue = newQueue.filter(
      (item) =>
        item?.video_id?.trim() &&
        item?.title?.trim() &&
        item?.thumbnail?.trim() &&
        item?.author?.trim()
    )

    socket.emit('updateQueue', { newQueue: filteredQueue, token, roomId })
  }

  const handlePlayQueueItem = (video) => {
    if (playerRef.current?.getVideoData()?.video_id === video.video_id) {
      return
    }

    setVideoId(null)
    setThumbnailImage({
      songTitle: video.title,
      songThumbnail: video.thumbnail,
      author_name: video.author,
    })

    setTimeout(() => {
      setVideoId(video.video_id)
      setResumeTime(0)
      setShouldPlayAfterSeek(true)
      setPendingAction(null)
      setShouldSeekAndPlay(true)

      socket.emit('playFromQueue', {
        videoId: video.video_id,
        time: 0,
        token,
        roomId,
      })
      socket.emit('saveState', {
        videoId: video.video_id,
        time: 0,
        isPlaying: true,
        roomId,
      })

      if (playerRef.current && typeof playerRef.current.unMute === 'function') {
        playerRef.current.unMute()
      }
    }, 100)
  }

  const onReady = useCallback((e) => {
    playerRef.current = e.target
    const interval = setInterval(() => {
      const player = playerRef.current
      if (player && player.getCurrentTime) {
        const currentTime = player.getCurrentTime()
        setCurrentTime(currentTime)
        setDuration(player.getDuration() || 0)
        // console.log(`Current Time Updated:${currentTime}`)
      }
    }, 250)

    return () => clearInterval(interval)
  }, [])

  const onPlayerReady = useCallback(
    (event) => {
      try {
        playerRef.current = event.target
        setIsPlayerReady(true)

        if (typeof volume === 'number') {
          playerRef.current.setVolume(volume)
        }

        onReady(event)
        if (pendingAction) {
          handleRemoteAction(pendingAction)
          setPendingAction(null)
        }

        if (resumeTime !== null && playerRef.current) {
          const player = playerRef.current
          player.seekTo(resumeTime, true)

          if (shouldPlayAfterSeek) {
            setTimeout(() => {
              player.mute()
              const result = player.playVideo()
              if (result?.then) {
                result
                  .then(() => {
                    setTimeout(() => player.unMute(), 1000)
                  })
                  .catch((err) => {
                    console.warn('Autoplay blocked by browser:', err)
                  })
              } else {
                setTimeout(() => player.unMute(), 1000)
              }
            }, 500)
          }
        }

        setResumeTime(null)
        setShouldPlayAfterSeek(false)
        setPendingSeek(false)
      } catch (err) {
        console.error('Error in onPlayerReady:', err)
      }
    },
    [
      pendingAction,
      handleRemoteAction,
      pendingSeek,
      resumeTime,
      shouldPlayAfterSeek,
      volume,
    ]
  )

  const handleDeleteVideo = (idToDelete) => {
    const isCurrentSong = idToDelete === videoId
    const newQueue = queue.filter(({ video_id }) => video_id !== idToDelete)
    setQueue(newQueue)

    socket.emit('updateQueue', { newQueue, token, roomId })

    if (isCurrentSong) {
      // Try to play the next song if possible
      const deletedIndex = queue.findIndex((v) => v.video_id === idToDelete)
      const nextSong = queue[deletedIndex + 1] || queue[deletedIndex - 1]

      if (nextSong) {
        handlePlayQueueItem(nextSong)
      } else {
        // No next song → stop playback
        setVideoId('')
        setResumeTime(null)
        setIsPlaying(false)
        setThumbnailImage({ songTitle: '', songThumbnail: '', author_name: '' })
      }
    }
  }

  const onPlayerStateChange = (event) => {
    if (!playerRef.current) return

    const player = playerRef.current
    const playerState = event.data
    const currentTime = player.getCurrentTime()

    if (playerState === window.YT.PlayerState.CUED && shouldSeekAndPlay) {
      if (resumeTime !== null) {
        playerRef.seekTo(resumeTime, true)
        try {
          if (shouldPlayAfterSeek) {
            player.mute()
            player.playVideo()
            setTimeout(() => {
              player.unMute()
            }, 1000)
          }
        } catch (e) {
          console.error('Error during seek/play:', e)
        }
        setShouldPlayAfterSeek(false)
        setResumeTime(null)
      }
    }

    if (playerState === window.YT.PlayerState.ENDED) {
      const currentIndex = queue.findIndex((v) => v.video_id === videoId)
      if (currentIndex !== -1 && currentIndex < queue.length - 1) {
        const nextSong = queue[currentIndex + 1]
        if (nextSong) {
          socket.emit('playFromQueue', {
            videoId: nextSong.video_id,
            time: 0, // start from the beginning
            token,
            roomId,
          })

          handlePlayQueueItem(nextSong)
        }
      } else {
        setIsPlaying(false)
      }
    } else if (playerState === window.YT.PlayerState.PAUSED) {
      setIsPlaying(false)
      if (userClickedPause) {
        socket.emit('controlAction', {
          videoId,
          type: 'Pause',
          time: currentTime,
          token,
          roomId,
        })
        socket.emit('saveState', {
          videoId,
          time: currentTime,
          isPlaying: false,
          roomId,
        })
      }
      if (shouldPlayAfterSeek) {
        player.playVideo()
        setShouldPlayAfterSeek(false)
      }
    } else if (playerState === window.YT.PlayerState.PLAYING) {
      setIsPlaying(true)
      if (typeof volume === 'number') {
        player.setVolume(volume)
      }
      if (resumeTime === null || Math.abs(currentTime - resumeTime) > 2) {
        setCurrentTime(currentTime)
        socket.emit('seek', { time: currentTime, roomId })
      }

      socket.emit('controlAction', {
        videoId,
        type: 'Play',
        time: currentTime,
        token,
        roomId,
      })
      socket.emit('saveState', {
        videoId,
        time: currentTime,
        isPlaying: true,
        roomId,
      })
    }
  }

  const logout = () => {
    const decoded = jwtDecode(token)
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email: decoded?.email }),
    }

    fetch(`${process.env.REACT_APP_SERVER_SOCKET}/api/user/logout`, config)
      .then((response) => response.json())
      .then((result) => {
        localStorage.removeItem('token')
        localStorage.removeItem('roomId')
        localStorage.removeItem('ytToken')
        localStorage.removeItem('profile')
        navigate('/login')
      })
      .catch((error) => console.error(error))
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && roomId) {
        // Tab is active, re-sync state
        socket.emit('requestFullSync', { roomId })
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [roomId])
  // this is for the sync state of the player
  useEffect(() => {
    if (!isPlaying || !roomId || !videoId) return

    const interval = setInterval(() => {
      const player = playerRef.current
      if (player && typeof player.getCurrentTime === 'function') {
        const time = player.getCurrentTime()
        socket.emit('saveState', {
          videoId,
          time,
          isPlaying: true,
          roomId,
        })
      }
    }, 1000) // every 1 seconds

    return () => clearInterval(interval)
  }, [isPlaying, roomId, videoId])

  useEffect(() => {
    if (autoPlayNext && isPlayerReady && videoId) {
      if (
        playerRef.current &&
        typeof playerRef.current.playVideo === 'function'
      ) {
        console.log('[autoPlayNext effect] Calling playVideo for', videoId)
        playerRef.current.playVideo()
        setIsPlaying(true)
        setAutoPlayNext(false)
      }
    }
  }, [autoPlayNext, isPlayerReady, videoId])
  const handleVolumeChange = (event, newValue) => {
    const newVolume = newValue
    setVolume(newVolume)

    if (playerRef.current) {
      playerRef.current.setVolume(newVolume)
      if (newVolume === 0) {
        setIsMuted(true)
      } else {
        setIsMuted(false)
      }
    }

    socket.emit('controlAction', {
      type: 'VolumeChange',
      value: newVolume,
      token,
      videoId,
      previousVolume: volume,
      roomId,
    })
  }
  const handleSeek = (time) => {
    if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(time, true)
      setCurrentTime(time) // Update state immediately
      socket.emit('seek', { time, roomId }) // Notify other clients
    }
  }

  if (!roomId) return null

  return (
    <YouTubePlayerUI
      roomId={roomId}
      logout={logout}
      handlePlayPauseClick={handlePlayPauseClick}
      onPlayerReady={onPlayerReady}
      onPlayerStateChange={onPlayerStateChange}
      handlePlayQueueItem={handlePlayQueueItem}
      handleClick={handleClick}
      handleVolumeChange={handleVolumeChange}
      handleAddVideo={handleAddVideo}
      handleDeleteVideo={handleDeleteVideo}
      videoId={videoId}
      isPlaying={isPlaying}
      isMuted={isMuted}
      volume={volume}
      newVideoInput={newVideoInput}
      setNewVideoInput={setNewVideoInput}
      queue={queue}
      thumbnailImage={thumbnailImage}
      setThumbnailImage={setThumbnailImage}
      setQueue={setQueue}
      playerRef={playerRef}
      currentTime={currentTime}
      setCurrentTime={setCurrentTime}
      seeking={seeking}
      setSeeking={setSeeking}
      duration={duration}
      setDuration={setDuration}
      handleSeek={handleSeek}
    />
  )
}
